import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { hasGoAppLicense } from '../../utils/userFinanceiroAccess';
import { sendDocumentationOryaQuery } from '../documentation/documentationOryaService';
import {
  buildConfigOryaContextPath,
  buildConfigOryaQuestionWithContext,
} from '../config-assistant/configOryaContext';
import { buildConfigHubCatalog, searchConfigHubCatalog } from './configHubCatalog';
import ConfigHubOryaPanel from './ConfigHubOryaPanel';
import './ConfigHubView.css';

function isMacPlatform() {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.platform || '');
}

function createMessageId() {
  return `hub-orya-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildOryaHistory(messages) {
  const items = (messages || []).filter((m) => !m.isError);
  const history = [];
  for (let i = 0; i + 1 < items.length; i += 2) {
    const user = items[i];
    const assistant = items[i + 1];
    if (user?.role !== 'user' || assistant?.role !== 'assistant') continue;
    const userContent = String(user.apiContent || user.content || '').trim();
    const assistantContent = String(assistant.content || '').trim();
    if (!userContent || !assistantContent) continue;
    history.push({ role: 'user', content: userContent });
    history.push({ role: 'assistant', content: assistantContent });
  }
  return history;
}

function sectionFromPathname(pathname) {
  const p = String(pathname || '');
  if (p.includes('/configuracoes/processos')) return 'process';
  if (p.includes('/configuracoes/go')) return 'go';
  if (p.includes('/configuracoes/customers')) return 'usersCustomers';
  if (p.includes('/configuracoes/usuarios')) return 'users';
  if (p.includes('/configuracoes/empresa')) return 'company';
  if (p.includes('/configuracoes/integracoes')) return 'integrations';
  if (p.includes('/configuracoes/painel-controle')) return 'painelControle';
  if (p === '/configuracoes' || p === '/configuracoes/') return 'hub';
  return 'hub';
}

/**
 * Busca de módulos/configs do hub — botão + palette (Ctrl/⌘ K)
 * com chaveador Pesquisa | Pergunta a IA.
 */
export default function ConfigHubSearch({
  currentUser,
  onNavigate,
  showStorage = false,
  configSection = null,
}) {
  const location = useLocation();
  const hasCustomers = Number(currentUser?.verve_customers_module) === 1;
  const hasGo = hasGoAppLicense(currentUser);
  const resolvedSection = configSection || sectionFromPathname(location.pathname);

  const catalog = useMemo(
    () => buildConfigHubCatalog({ hasCustomers, hasGo, showStorage }),
    [hasCustomers, hasGo, showStorage]
  );

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('search'); // search | ai
  const [query, setQuery] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [aiSending, setAiSending] = useState(false);
  const [aiBodyReady, setAiBodyReady] = useState(false);
  const inputRef = useRef(null);
  const isMac = isMacPlatform();

  const results = useMemo(() => searchConfigHubCatalog(catalog, query), [catalog, query]);
  const contextPath = useMemo(
    () => buildConfigOryaContextPath({ section: resolvedSection }),
    [resolvedSection]
  );
  const userDisplayName = currentUser?.name || currentUser?.username || '';
  const hasAiChat = aiMessages.length > 0 || aiSending;

  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (mode !== 'ai') {
      el.style.height = '22px';
      return;
    }
    const next = Math.min(el.scrollHeight, 120);
    el.style.height = `${Math.max(next, 22)}px`;
  }, [mode]);

  useEffect(() => {
    const onKey = (e) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && String(e.key || '').toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setMode('search');
        setQuery('');
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isMac]);

  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onEsc, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'ai') {
      setAiBodyReady(false);
      return undefined;
    }
    setAiBodyReady(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAiBodyReady(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    resizeInput();
  }, [mode, open, resizeInput]);

  useEffect(() => {
    resizeInput();
  }, [query, resizeInput]);

  const handleSelect = (href) => {
    setOpen(false);
    setQuery('');
    onNavigate?.(href);
  };

  const closePalette = () => {
    setOpen(false);
    setQuery('');
  };

  const submitAi = useCallback(async () => {
    const text = String(query || '').trim();
    if (!text || aiSending) return;

    const apiQuestion = buildConfigOryaQuestionWithContext(text, contextPath);
    const userMsg = {
      id: createMessageId(),
      role: 'user',
      content: text,
      apiContent: apiQuestion,
    };
    const history = buildOryaHistory(aiMessages);

    setAiMessages((prev) => [...prev, userMsg]);
    setQuery('');
    setAiSending(true);

    try {
      const data = await sendDocumentationOryaQuery({
        question: apiQuestion,
        history,
      });
      const answer = String(data?.answer || '').trim();
      setAiMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'assistant',
          content: answer || 'Não encontrei uma resposta na documentação.',
          sources: Array.isArray(data?.sources_used) ? data.sources_used : [],
        },
      ]);
    } catch (err) {
      setAiMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'assistant',
          content: err?.message || 'Erro ao consultar a Orya.',
          isError: true,
        },
      ]);
    } finally {
      setAiSending(false);
      inputRef.current?.focus();
    }
  }, [query, aiSending, aiMessages, contextPath]);

  const onInputKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (mode === 'ai') {
      if (e.shiftKey) return;
      e.preventDefault();
      submitAi();
      return;
    }
    e.preventDefault();
    if (results[0]?.href) handleSelect(results[0].href);
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setQuery('');
  };

  return (
    <>
      <button
        type="button"
        className="config-hub-search-btn"
        onClick={() => {
          setOpen(true);
          setMode('search');
          setQuery('');
        }}
        aria-label="Buscar em configurações"
      >
        <span className="config-hub-search-ico" aria-hidden>
          ⌕
        </span>
        <span className="config-hub-search-label">Buscar configurações...</span>
        <span className="config-hub-kbd">{isMac ? '⌘K' : 'Ctrl K'}</span>
      </button>

      {open
        ? createPortal(
            <div
              className="config-hub-palette-scrim"
              role="presentation"
              onClick={closePalette}
            >
              <div
                className={[
                  'config-hub-palette',
                  mode === 'ai' ? 'is-ai-mode' : '',
                  mode === 'ai' && hasAiChat ? 'is-ai-chat' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="dialog"
                aria-modal="true"
                aria-label={mode === 'ai' ? 'Pergunta a IA' : 'Buscar configurações'}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`config-hub-palette-head${mode === 'ai' ? ' is-ai' : ''}`}>
                  <span className="config-hub-palette-head-ico" aria-hidden>
                    {mode === 'ai' ? '✦' : '⌕'}
                  </span>
                  <div className="config-hub-palette-input-wrap">
                    {mode === 'ai' && !String(query || '').trim() ? (
                      <span className="config-hub-palette-input-ph" aria-hidden>
                        Pergunte à Orya sobre configurações…
                      </span>
                    ) : null}
                    <textarea
                      ref={inputRef}
                      className={`config-hub-palette-input${mode === 'ai' ? ' is-multiline' : ''}`}
                      value={query}
                      rows={1}
                      placeholder={
                        mode === 'ai' ? '' : 'Buscar módulos e configurações…'
                      }
                      onChange={(e) => setQuery(e.target.value)}
                      onInput={resizeInput}
                      onKeyDown={onInputKeyDown}
                      aria-label={mode === 'ai' ? 'Pergunta para a Orya' : 'Buscar'}
                      disabled={mode === 'ai' && aiSending}
                    />
                  </div>
                  <div
                    className="config-hub-mode-switch"
                    role="tablist"
                    aria-label="Modo da palette"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === 'search'}
                      className={`config-hub-mode-switch-btn${mode === 'search' ? ' is-active' : ''}`}
                      onClick={() => switchMode('search')}
                    >
                      Pesquisa
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === 'ai'}
                      className={`config-hub-mode-switch-btn${mode === 'ai' ? ' is-active' : ''}`}
                      onClick={() => switchMode('ai')}
                    >
                      Pergunta a IA
                    </button>
                  </div>
                  <span className="config-hub-kbd">ESC</span>
                </div>

                {mode === 'search' ? (
                  <div className="config-hub-palette-body">
                    {results.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className="config-hub-palette-row"
                        onClick={() => handleSelect(r.href)}
                      >
                        <span className="config-hub-palette-kind">{r.kind}</span>
                        <span className="config-hub-palette-label">{r.label}</span>
                        <span className="config-hub-palette-path">{r.pathLabel}</span>
                      </button>
                    ))}
                    {!results.length ? (
                      <div className="config-hub-palette-empty">
                        Nenhum resultado para “{query}”.
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className={[
                      'config-hub-palette-body',
                      'config-hub-palette-body--orya',
                      aiBodyReady ? 'is-ready' : '',
                      hasAiChat ? 'is-chat' : 'is-welcome',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <ConfigHubOryaPanel
                      messages={aiMessages}
                      sending={aiSending}
                      userDisplayName={userDisplayName}
                    />
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
